/*
  One confirmation for "get rid of this account", which asks the server what is
  attached before it decides what to offer.

  An account with no live adverts can simply be deleted. An account WITH live
  adverts cannot -- and refusing with "user has active ads" is useless, because
  every duplicate worth deleting has live adverts. So in that case the dialog
  asks for the account number of the account being kept and moves everything
  across first.
*/
import Swal from "sweetalert2";
import { ApiURLs } from "../constant/ApiUrls";
import { fetchData, patchData, postData } from "./api";

const esc = (s) =>
  String(s == null ? "" : s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );

const nameOf = (row) => row.business_name || row.name || `account ${row.id}`;

/* Plain-English summary of what is attached, so nobody has to guess what a
   merge would carry across. */
const describe = (fp) => {
  const bits = [];
  if (fp.ads_total) {
    bits.push(
      `${fp.ads_total} advert${fp.ads_total === 1 ? "" : "s"}` +
        (fp.ads_live ? ` (${fp.ads_live} live)` : " (none live)")
    );
  }
  const others = (fp.attached || []).filter((a) => a.table !== "place_ads");
  const rows = others.reduce((s, a) => s + Number(a.rows || 0), 0);
  if (rows) bits.push(`${rows} other record${rows === 1 ? "" : "s"}`);
  return bits.length ? bits.join(", ") : "nothing attached";
};

export const confirmDeleteOrMerge = async (row, reload) => {
  const who = nameOf(row);

  let fp = null;
  try {
    const r = await fetchData(`${ApiURLs.ACCOUNT_FOOTPRINT}/${row.id}/footprint`);
    fp = r && r.data ? r.data : null;
  } catch (e) {
    // If the server cannot say, fall back to the plain confirmation. It will
    // still be refused server-side if the account has live adverts.
    fp = null;
  }

  const live = Number((fp && fp.ads_live) || 0);

  /* ---- nothing live: an ordinary delete -------------------------------- */
  if (!live) {
    const detail = fp ? describe(fp) : "";
    const result = await Swal.fire({
      title: "Delete this account?",
      html:
        `<div style="text-align:left;font-size:14px;line-height:1.5">` +
        `<b>${esc(who)}</b>` +
        (row.account_number
          ? `<br><span style="font-family:monospace;color:#666">Account No: ${esc(
              row.account_number
            )}</span>`
          : "") +
        (detail ? `<br><span style="color:#666">${esc(detail)}</span>` : "") +
        `<hr style="margin:10px 0">` +
        `The account number stays retired and is never given to anyone else.` +
        `</div>`,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      confirmButtonText: "Yes, delete it",
      confirmButtonColor: "#eb5757",
      cancelButtonColor: "gray",
      reverseButtons: true,
      focusCancel: true,
      width: 480,
    });
    if (!result.isConfirmed) return;
    const response = await patchData(`${ApiURLs.DELETE_USER}/${row.id}`);
    if (response && response.status === 1 && reload) reload();
    return;
  }

  /* ---- live adverts: offer the merge ----------------------------------- */
  const result = await Swal.fire({
    title: "This account has live adverts",
    html:
      `<div style="text-align:left;font-size:14px;line-height:1.5">` +
      `<b>${esc(who)}</b>` +
      (row.account_number
        ? `<br><span style="font-family:monospace;color:#666">Account No: ${esc(
            row.account_number
          )}</span>`
        : "") +
      `<br><span style="color:#666">${esc(describe(fp))}</span>` +
      `<hr style="margin:10px 0">` +
      `An account cannot be deleted while its adverts are live, because the ` +
      `adverts would go with it.<br><br>` +
      `If this is a duplicate, type the account number of the one you are ` +
      `<b>keeping</b>. Everything above moves onto it and this account is then ` +
      `removed.` +
      `<input id="merge-target" class="swal2-input" style="margin-top:12px" ` +
      `placeholder="Account number to keep, e.g. 000726" autocomplete="off">` +
      `<div id="merge-found" style="min-height:18px;color:#00a651;font-size:13px"></div>` +
      `</div>`,
    showCancelButton: true,
    cancelButtonText: "Cancel",
    confirmButtonText: "Move everything and delete this one",
    confirmButtonColor: "#eb5757",
    cancelButtonColor: "gray",
    reverseButtons: true,
    focusCancel: true,
    width: 540,
    didOpen: () => {
      const input = document.getElementById("merge-target");
      const out = document.getElementById("merge-found");
      let timer = null;
      input.addEventListener("input", () => {
        clearTimeout(timer);
        const v = input.value.trim();
        out.style.color = "#00a651";
        out.textContent = "";
        if (!v) return;
        timer = setTimeout(async () => {
          try {
            const r = await fetchData(`${ApiURLs.ACCOUNT_BY_NUMBER}/${encodeURIComponent(v)}`);
            const u = r && r.data;
            if (u && Number(u.id) !== Number(row.id)) {
              out.textContent = `Keeping: ${u.business_name || u.name}`;
            } else if (u) {
              out.style.color = "#eb5757";
              out.textContent = "That is this same account.";
            }
          } catch (e) {
            out.style.color = "#eb5757";
            out.textContent = "No account has that number.";
          }
        }, 400);
      });
    },
    preConfirm: async () => {
      const v = (document.getElementById("merge-target").value || "").trim();
      if (!v) {
        Swal.showValidationMessage("Type the account number you are keeping");
        return false;
      }
      try {
        const r = await fetchData(`${ApiURLs.ACCOUNT_BY_NUMBER}/${encodeURIComponent(v)}`);
        const u = r && r.data;
        if (!u) throw new Error("not found");
        if (Number(u.id) === Number(row.id)) {
          Swal.showValidationMessage("That is this same account");
          return false;
        }
        return { number: v, name: u.business_name || u.name };
      } catch (e) {
        Swal.showValidationMessage("No account has that number");
        return false;
      }
    },
  });

  if (!result.isConfirmed || !result.value) return;

  // Second confirmation, naming both sides. Moving a dealer's whole stock is not
  // something to do on one click of a red button.
  const sure = await Swal.fire({
    title: "Move and delete?",
    html:
      `<div style="text-align:left;font-size:14px;line-height:1.5">` +
      `Everything on <b>${esc(who)}</b> moves to <b>${esc(result.value.name)}</b> ` +
      `(${esc(result.value.number)}), and <b>${esc(who)}</b> is then deleted.` +
      `<br><br>This cannot be undone from here.</div>`,
    showCancelButton: true,
    cancelButtonText: "Cancel",
    confirmButtonText: "Do it",
    confirmButtonColor: "#eb5757",
    cancelButtonColor: "gray",
    reverseButtons: true,
    focusCancel: true,
    width: 500,
  });
  if (!sure.isConfirmed) return;

  const response = await postData(
    { target_account_number: result.value.number },
    `${ApiURLs.ACCOUNT_MERGE}/${row.id}/merge`
  );

  if (response && response.status === 1) {
    const d = response.data || {};
    await Swal.fire({
      icon: "success",
      title: "Merged",
      html:
        `<div style="text-align:left;font-size:14px;line-height:1.5">` +
        `${d.rows_moved || 0} record${d.rows_moved === 1 ? "" : "s"} moved to ` +
        `<b>${esc(result.value.name)}</b>.` +
        (d.rows_dropped_as_duplicate
          ? `<br>${d.rows_dropped_as_duplicate} were already there and were dropped.`
          : "") +
        `<br><br>Account number ${esc(
          (d.source && d.source.account_number) || row.account_number || ""
        )} is retired and will never be reissued.</div>`,
      width: 500,
    });
    if (reload) reload();
  }
};

export default confirmDeleteOrMerge;
